import type { AppDispatch } from "index";
import {
  emailBuilderCloseInlineContent,
  emailBuilderOpenBlockSettings,
} from "redux/actions/emailBuilder/emailBuilder";

export const EMAIL_BUILDER_BLOCK_ANCHOR_ATTR = "data-email-builder-block-id";
export const EMAIL_BUILDER_BLOCK_ACTIONS_ATTR = "data-email-builder-block-actions";
export {
  EMAIL_BUILDER_ACTION_RAIL_ATTR,
  EMAIL_BUILDER_ACTION_RAIL_SELECTOR,
} from "../../../canvas/actionRailConstants";
export const EMAIL_BUILDER_SETTINGS_TRIGGER_ATTR = "data-email-builder-settings-trigger";
export const EMAIL_BUILDER_SECTION_SETTINGS_TRIGGER_ATTR = "data-email-builder-section-settings-trigger";
export const EMAIL_BUILDER_LAYOUT_CELL_ID_ATTR = "data-email-builder-layout-cell-id";
export const EMAIL_BUILDER_LINK_TRIGGER_ATTR = "data-email-builder-link-trigger";

export type TEmailBuilderSettingsFocusTarget = "settings-trigger" | "block-group";

export type TEmailBuilderInlineTextLinkFocusTarget = "editor" | "link-trigger";

export function isLiveEmailBuilderFocusControl(
  element: HTMLElement | null,
): element is HTMLElement {
  if (element === null) {
    return false;
  }

  if (!element.isConnected) {
    return false;
  }

  if (element.hasAttribute("disabled")) {
    return false;
  }

  if (element.getAttribute("aria-hidden") === "true") {
    return false;
  }

  if (element.closest("[inert]") !== null) {
    return false;
  }

  return true;
}

export function resolveEmailBuilderSettingsFocusTarget(args: {
  settingsTriggerLive: boolean;
  blockGroupLive: boolean;
}): TEmailBuilderSettingsFocusTarget | null {
  if (args.settingsTriggerLive) {
    return "settings-trigger";
  }

  if (args.blockGroupLive) {
    return "block-group";
  }

  return null;
}

export function resolveEmailBuilderInlineTextLinkFocusTarget(args: {
  editorLive: boolean;
  linkTriggerLive: boolean;
}): TEmailBuilderInlineTextLinkFocusTarget | null {
  if (args.editorLive) {
    return "editor";
  }

  if (args.linkTriggerLive) {
    return "link-trigger";
  }

  return null;
}

export function restoreEmailBuilderSettingsFocus(
  blockId: string,
): TEmailBuilderSettingsFocusTarget | null {
  const trigger = window.document.querySelector<HTMLElement>(
    `[${EMAIL_BUILDER_SETTINGS_TRIGGER_ATTR}="${blockId}"]`,
  );
  const blockGroup = window.document.querySelector<HTMLElement>(
    `[${EMAIL_BUILDER_BLOCK_ANCHOR_ATTR}="${blockId}"]`,
  );
  const target = resolveEmailBuilderSettingsFocusTarget({
    settingsTriggerLive: isLiveEmailBuilderFocusControl(trigger),
    blockGroupLive: isLiveEmailBuilderFocusControl(blockGroup),
  });

  if (target === "settings-trigger" && trigger !== null) {
    trigger.focus();
    return target;
  }

  if (target === "block-group" && blockGroup !== null) {
    blockGroup.focus();
    return target;
  }

  return null;
}

export function restoreEmailBuilderCanvasHostFocus(): void {
  const host = window.document.getElementById("email-builder-main");
  if (isLiveEmailBuilderFocusControl(host)) {
    host.focus({ preventScroll: true });
  }
}

export function releaseEmailBuilderCanvasKeyboardFocus(): void {
  const active = window.document.activeElement;
  if (active instanceof HTMLElement) {
    active.blur();
  }

  window.requestAnimationFrame(() => {
    restoreEmailBuilderCanvasHostFocus();
  });
}

export function exitInlineContentToDesktopSettings(
  dispatch: AppDispatch,
  blockId: string | null,
  usesDesktopRightSettingsPanel: boolean,
): void {
  dispatch(emailBuilderCloseInlineContent());
  if (!usesDesktopRightSettingsPanel || blockId === null) {
    return;
  }

  dispatch(emailBuilderOpenBlockSettings());
  window.requestAnimationFrame(() => {
    restoreEmailBuilderSettingsFocus(blockId);
  });
}

export function restoreEmailBuilderSectionSettingsFocus(sectionId: string): void {
  const trigger = window.document.querySelector<HTMLElement>(
    `[${EMAIL_BUILDER_SECTION_SETTINGS_TRIGGER_ATTR}="${sectionId}"]`,
  );
  if (isLiveEmailBuilderFocusControl(trigger)) {
    trigger.focus();
    return;
  }

  const section = window.document.querySelector<HTMLElement>(
    `[data-email-builder-section-id="${sectionId}"]`,
  );
  if (isLiveEmailBuilderFocusControl(section)) {
    section.focus();
  }
}
